import type { ItemCarrinho, PassoCarrinho, SubitemCarrinho } from '../store/carrinho-slice'
import type { FaixaPrecoProduto, TipoCalculoPasso } from '../api'
import type { ItemPedidoListado, SubitemPedido } from '../components/lista-itens-pedido'
import { ehBlocoDeAdicionais } from './adicional-por-sabor'
import { formatarBRL } from './formato';
import { proximaFaixaVantajosa } from './preco-por-quantidade';

/**
 * Converte os itens do carrinho para o formato da listagem compartilhada
 * (`ListaItensPedido`).
 *
 * Dois detalhes que valem lembrar: `precoUnitario` do carrinho JÁ inclui os
 * complementos, então o total da linha é ele × quantidade — o "+R$" que aparece
 * em cada complemento é informativo, não entra na soma de novo. E `editavel`
 * sai da existência de passos: item sem passo não tem o que reabrir.
 */
export function itensCarrinhoParaListagem(
  itens: ItemCarrinho[],
  imagens?: Map<number, string | undefined>,
  tiposCalculo?: Map<number, TipoCalculoPasso>,
): ItemPedidoListado[] {
  return itens.map((item) => ({
    id: item.id,
    nome: item.nome,
    quantidade: item.quantidade,
    valorTotal: item.precoUnitario * item.quantidade,
    imagemUrl: imagens?.get(item.idProduto),
    observacao: item.observacao ?? null,
    editavel: (item.passos?.length ?? 0) > 0,
    avisoFaixa: convitePorQuantidade(item),
    subitens: subitensDosPassos(item.passos ?? [], tiposCalculo),
  }));
}

/** Adicionais de um sabor pelo que foi cobrado: o `valorPasso` do bloco já traz a fração, e o rateio a devolve a cada um. */
function adicionaisDoBloco(bloco: PassoCarrinho, recuo: boolean): SubitemPedido[] {
  const cheio = bloco.itens.reduce((s, sub) => s + sub.valor * sub.quantidade, 0)
  const fracao = cheio > 0 && typeof bloco.valorPasso === 'number' ? bloco.valorPasso / cheio : 1

  return bloco.itens
    .filter((sub) => sub.quantidade > 0)
    .map((sub) => ({ nome: sub.nome ?? 'Item', quantidade: sub.quantidade, valorTotal: sub.valor * sub.quantidade * fracao, recuo }))
}

function mdc(a: number, b: number): number {
  return b === 0 ? a : mdc(b, a % b)
}

/** "1/2", "1/3", "2/3": a parte que a opção ocupa no passo (quantidade dela ÷ soma do passo, a regra do wizard). */
function textoDaFracao(quantidade: number, total: number): string | undefined {
  if (!Number.isInteger(quantidade) || !Number.isInteger(total) || quantidade <= 0 || quantidade >= total) return undefined

  const divisor = mdc(quantidade, total)

  return `${quantidade / divisor}/${total / divisor}`
}

/** Mesma conta do `ratearPasso` do backend: o que a opção pesa no `valorPasso`, e as linhas somam o passo. */
function valorRateado(passo: PassoCarrinho, sub: SubitemCarrinho): number {
  const somaCrua = passo.itens.reduce((s, it) => s + it.valor * it.quantidade, 0)
  const contribuicao = passo.valorPasso ?? somaCrua
  const peso = somaCrua > 0 ? (sub.valor * sub.quantidade) / somaCrua : 1 / passo.itens.length

  return contribuicao * peso
}

/** Opção de passo FRACAO ou MAIOR_VALOR: "1/2 Calabresa" pelo que pesa no preço. */
function opcaoFracionada(passo: PassoCarrinho, sub: SubitemCarrinho, total: number): SubitemPedido {
  const nome = sub.nome ?? 'Item'
  const valorTotal = valorRateado(passo, sub)

  // Ocupando a pizza inteira ela é uma pizza desse sabor, não "2×" pelas duas metades.
  if (sub.quantidade >= total) return { nome, quantidade: 1, valorTotal }

  const fracao = textoDaFracao(sub.quantidade, total)

  return fracao ? { nome, quantidade: sub.quantidade, valorTotal, fracao } : { nome, quantidade: sub.quantidade, valorTotal }
}

/** Complementos na ordem do pedido, com os adicionais de cada sabor logo abaixo dele. */
function subitensDosPassos(passos: PassoCarrinho[], tiposCalculo?: Map<number, TipoCalculoPasso>): SubitemPedido[] {
  const blocos = passos.filter(ehBlocoDeAdicionais)
  const listados = new Set<PassoCarrinho>()
  const subitens: SubitemPedido[] = []

  for (const passo of passos) {
    if (ehBlocoDeAdicionais(passo)) continue

    // Sem o tipo (carrinho antigo, catálogo ainda não veio) a linha fica como sempre foi.
    const tipo = tiposCalculo?.get(passo.idProdutoPasso)
    const fracionado = tipo === 'FRACAO' || tipo === 'MAIOR_VALOR'
    const total = passo.itens.reduce((s, sub) => s + Math.max(sub.quantidade, 0), 0)

    for (const sub of passo.itens) {
      if (sub.quantidade <= 0) continue

      subitens.push(fracionado
        ? opcaoFracionada(passo, sub, total)
        : { nome: sub.nome ?? 'Item', quantidade: sub.quantidade, valorTotal: sub.valor * sub.quantidade })

      for (const bloco of blocos) {
        if (bloco.idProdutoPassoPai !== passo.idProdutoPasso || bloco.idItemPai !== sub.idTabelaPrecoItem) continue

        listados.add(bloco)
        subitens.push(...adicionaisDoBloco(bloco, true))
      }
    }
  }

  // Bloco cujo sabor não está na linha não some da tela: ele está no preço.
  for (const bloco of blocos) {
    if (!listados.has(bloco)) subitens.push(...adicionaisDoBloco(bloco, false))
  }

  return subitens
}

export function convitePorQuantidade(linha: { faixasPreco?: FaixaPrecoProduto[]; quantidade: number; precoUnitario: number; }): string | null {
  const proxima = proximaFaixaVantajosa(linha.faixasPreco, linha.quantidade, linha.precoUnitario);
  if (!proxima) return null;

  const unidades = proxima.faltam === 1 ? '1 unidade' : `${proxima.faltam} unidades`;
  return `Leve mais ${unidades} e pague ${formatarBRL(proxima.precoUnitario)} cada`;
}
